import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { GradientCircularProgress } from "./utils/Loading";
import { getUserId } from "./utils/Utils";
import { handleSendCommand } from "./App"

const WebPlayer = ({ currentTheme, environments, currentPlaylist, selectedTrack = '1', t, 
  isPlaying, setIsPlaying, house, volumeRef, loading, setLoading, montageOrderSignature }) => {
  const containerRef = useRef(null);
  const abortController = useRef(new AbortController());
  const videoElement = useRef(null);
  const initiatedRef = useRef(false);
  const effectCountRef = useRef(0);
  const cleanupInProgressRef = useRef(false);
  const lastVolumeTime = useRef(0);
  const currentTrackRef = useRef(selectedTrack);
  const [instanceId] = useState(() => Date.now().toString(36) + Math.random().toString(36).substring(2, 5));

  console.log('[WebPlayer isPlaying] starting', isPlaying);

  // Update track ref when selectedTrack changes
  useEffect(() => {
    currentTrackRef.current = selectedTrack;
  }, [selectedTrack]);

  // Safe console logging with instance ID for debugging
  const logInfo = (message, ...args) => {
    console.log(`[WebPlayer-${instanceId}] ${message}`, ...args);
  };

  const logWarn = (message, ...args) => {
    console.warn(`[WebPlayer-${instanceId}] ${message}`, ...args);
  };

  const logError = (message, ...args) => {
    console.error(`[WebPlayer-${instanceId}] ${message}`, ...args);
  };

  // Function to apply volume with debouncing
  const applyVolume = () => {
    if (!videoElement.current) {
      logWarn("Cannot apply volume - video element not found");
      return;
    }

    if (cleanupInProgressRef.current) {
      logWarn("Cannot apply volume - cleanup in progress");
      return;
    }

    if (volumeRef && volumeRef.current !== undefined && house) {
      const now = Date.now();
      // Only send volume command if enough time has passed (300ms)
      if (now - lastVolumeTime.current > 300) {
        logInfo(`Applying volume: ${volumeRef.current}`);
        handleSendCommand(`<vlc><cmd action="volume" param="${volumeRef.current}"/></vlc>`, house);
        lastVolumeTime.current = now;
        
        // Also try to directly set volume on the HTML5 video element as a backup
        try {
          if (videoElement.current) {
            // Check if the video element is still valid and has volume property
            if (typeof videoElement.current.volume !== 'undefined') {
              videoElement.current.volume = volumeRef.current / 100;
              logInfo(`Set HTML5 video volume to ${volumeRef.current / 100}`);
            } else {
              logWarn("Video element doesn't have volume property");
            }
          }
        } catch (err) {
          logWarn("Error setting HTML5 video volume", err);
        }
      }
    } else {
      logWarn('Missing volume or house ref', { 
        volumeExists: !!volumeRef, 
        volumeValue: volumeRef?.current,
        houseExists: !!house 
      });
    }
  };

  // Clean up previous player before creating a new one
  const cleanupPreviousPlayer = () => {
    console.log('[WebPlayer isPlaying] cleanup start', isPlaying);
    // Set flag to prevent concurrent operations during cleanup
    cleanupInProgressRef.current = true;
    logInfo("Starting cleanup");
    
    try {
      // Reset playing status first
      setIsPlaying(false);
      
      if (containerRef.current) {
        // Find and clean up all video elements
        const videos = containerRef.current.querySelectorAll('video');
        if (videos.length) {
          logInfo(`Found ${videos.length} video elements to clean up`);
          
          // Clean up each video
          videos.forEach((video, index) => {
            try {
              logInfo(`Stopping video #${index + 1}`);
              
              // First pause the video
              try { video.pause(); } catch (e) { logWarn(`Error pausing video #${index + 1}`, e); }
              
              // Remove all event listeners (use the capture phase for all)
              const eventsToClear = ['play', 'playing', 'pause', 'ended', 'error', 'waiting', 'canplay', 'loadedmetadata'];
              for (const event of eventsToClear) {
                try { 
                  // Clone the node to remove all event listeners
                  const newVideo = video.cloneNode(true);
                  if (video.parentNode) {
                    video.parentNode.replaceChild(newVideo, video);
                  }
                } catch (e) { 
                  logWarn(`Error removing event listeners for ${event} on video #${index + 1}`, e); 
                }
              }
              
              // Set source to empty and force unload
              try { 
                video.src = ''; 
                video.removeAttribute('src');
                video.load();
              } catch (e) { 
                logWarn(`Error clearing source on video #${index + 1}`, e); 
              }
              
              // Try to remove from DOM
              try {
                if (video.parentNode) {
                  // Check if the node is still actually a child before attempting removal
                  if (Array.from(video.parentNode.children).includes(video)) {
                    video.parentNode.removeChild(video);
                    logInfo(`Successfully removed video #${index + 1} from DOM`);
                  } else {
                    logInfo(`Video #${index + 1} already removed from DOM`);
                  }
                }
              } catch (e) {
                logWarn(`Error removing video #${index + 1} from DOM`, e);
              }
            } catch (err) {
              logWarn(`General error cleaning up video #${index + 1}`, err);
            }
          });
        } else {
          logInfo("No video elements found during cleanup");
        }
        
        // Clear the container after handling specific elements
        try {
          // Instead of replacing the container, just clear its contents
          // This is safer with React's virtual DOM reconciliation
          if (containerRef.current) {
            containerRef.current.innerHTML = '';
            logInfo("Cleared container innerHTML");
          }
        } catch (e) {
          logError("Failed to clear container", e);
        }
      }
      
      // Reset video reference
      videoElement.current = null;
      logInfo("Reset video element reference");
    } catch (error) {
      logError("Error in cleanup function", error);
    } finally {
      // Reset cleanup flag when done
      cleanupInProgressRef.current = false;
      logInfo("Cleanup completed");
    }
  };

  // Main effect for loading and initializing the player
  useEffect(() => {
    const effectId = ++effectCountRef.current;
    logInfo(`Effect #${effectId} triggered for playlist/track change`);
    
    // Prevent running the effect if cleanup is in progress
    if (cleanupInProgressRef.current) {
      logWarn(`Effect #${effectId} skipped because cleanup is in progress`);
      return;
    }
    
    // Set loading state
    setLoading(true);
    
    // Clean up previous player first
    cleanupPreviousPlayer();
    
    const debounceDelay = 500;
    initiatedRef.current = true;
    
    const handler = setTimeout(async () => {
      logInfo(`Effect #${effectId} handler running after debounce`);
      
      try {
        // Signal that webplayer is starting initialization
        logInfo("Starting initialization");
        
        // Handle WallmuseInit coordination
        if (!window.WallmuseInit) {
          logWarn("WallmuseInit not found! Continuing without coordination");
        } else {
          logInfo("WallmuseInit found with states:", {
            playerReady: window.WallmuseInit._playerReady,
            webplayerReady: window.WallmuseInit._webplayerReady
          });
          
          // Only wait for player if it's not already ready
          if (!window.WallmuseInit._playerReady) {
            try {
              logInfo("Waiting for player to be ready");
              await Promise.race([
                window.WallmuseInit.playerReady(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout waiting for player')), 3000))
              ]);
              logInfo("Player is ready, proceeding");
            } catch (error) {
              logWarn("Timeout waiting for player, proceeding anyway:", error);
            }
          } else {
            logInfo("Player already ready, proceeding immediately");
          }
        }

        // If cleanup started during wait, abort
        if (cleanupInProgressRef.current) {
          logWarn(`Effect #${effectId} aborted because cleanup started during initialization`);
          return;
        }

        // Fetch player HTML
        const sessionId = getUserId();
        const existingEnvironmentId = environments[0]?.id;
        console.log('[WebPlayer] existingEnvironmentId: ',existingEnvironmentId);
        const anticache = Date.now();
        const currentTrack = currentTrackRef.current || '1';
        const url = `/wp-content/themes/neve-child-master/wm-player/index.html?session=${sessionId}&anticache=${anticache}&track=${currentTrack}`;
        // const url = `/wp-content/themes/neve-child-master/wm-playerB/index.html?session=${sessionId}&anticache=${anticache}&track=${currentTrack}`;

        logInfo(`Loading player with URL: ${url}`);
        
        const response = await fetch(url, {
          signal: abortController.current.signal,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // If cleanup started during fetch, abort
        if (cleanupInProgressRef.current) {
          logWarn(`Effect #${effectId} aborted because cleanup started during fetch`);
          return;
        }

        const html = await response.text();

        if (!containerRef.current) {
          throw new Error("Container ref is null after fetch");
        }

        // Load CSS first
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const cssLink = tempDiv.querySelector('link[rel="stylesheet"]');
        if (cssLink) {
          logInfo("Loading CSS");
          const linkPromise = new Promise((resolve) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = cssLink.href;
            link.onload = resolve;
            document.head.appendChild(link);
          });
          await linkPromise;
        }

        // If cleanup started during CSS load, abort
        if (cleanupInProgressRef.current) {
          logWarn(`Effect #${effectId} aborted because cleanup started during CSS load`);
          return;
        }

        // Inject HTML
        logInfo("Injecting HTML");
        containerRef.current.innerHTML = html;
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Add a global variable with the track parameter
        window.SELECTED_TRACK = currentTrackRef.current || '1';
        logInfo(`Set global track parameter: ${window.SELECTED_TRACK}`);

        // Load the main script
        const mainScript = containerRef.current.querySelector('script[defer]');
        logInfo(`Found main script: ${mainScript ? 'Yes' : 'No'}`);
        
        if (mainScript) {
          await new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = mainScript.src;
            script.onload = resolve;
            document.body.appendChild(script);
          });
        }

        // If cleanup started during script load, abort
        if (cleanupInProgressRef.current) {
          logWarn(`Effect #${effectId} aborted because cleanup started during script load`);
          return;
        }

        // Setup video monitoring with retry mechanism
        let foundVideo = false;
        let retryCount = 0;
        const maxRetries = 50;
        const retryInterval = 150; // ms
        
        const findAndSetupVideo = () => {
          if (foundVideo || retryCount >= maxRetries || cleanupInProgressRef.current) {
            return;
          }
          
          retryCount++;
          const video = containerRef.current?.querySelector('video');
          
          if (video) {
            logInfo(`Found video element on retry #${retryCount}`);
            foundVideo = true;
            videoElement.current = video;
            setupVideoMonitoring(video);
          } else if (retryCount < maxRetries) {
            logInfo(`No video found on retry #${retryCount}, will retry in ${retryInterval}ms`);
            setTimeout(findAndSetupVideo, retryInterval);
          } else {
            logWarn(`Failed to find video after ${maxRetries} retries`);
          }
        };
        
        // Start looking for video
        findAndSetupVideo();
        
        // Also use MutationObserver as a backup
        const videoObserver = new MutationObserver((mutations) => {
          if (foundVideo || cleanupInProgressRef.current) {
            videoObserver.disconnect();
            return;
          }
          
          for (const mutation of mutations) {
            if (mutation.addedNodes.length > 0) {
              const video = containerRef.current?.querySelector('video');
              if (video && !foundVideo) {
                logInfo("Video element found via observer");
                foundVideo = true;
                videoElement.current = video;
                setupVideoMonitoring(video);
                videoObserver.disconnect();
                return;
              }
            }
          }
        });
        
        if (containerRef.current && !foundVideo) {
          videoObserver.observe(containerRef.current, {
            childList: true,
            subtree: true
          });
          
          // Check immediately as well
          const video = containerRef.current.querySelector('video');
          if (video && !foundVideo) {
            logInfo("Video element found immediately");
            foundVideo = true;
            videoElement.current = video;
            setupVideoMonitoring(video);
            videoObserver.disconnect();
          } else {
            logInfo("No video element found initially, waiting via observer and retries");
          }
        }

        // Signal that webplayer is ready - wait a bit to ensure we've had time to find the video
        setTimeout(() => {
          if (window.WallmuseInit && !cleanupInProgressRef.current) {
            logInfo("Initialization complete, signaling ready");
            window.WallmuseInit.ready('webplayer');
          }
        }, 300);
        
      } catch (error) {
        logError("Error in useEffect handler:", error);
        setLoading(false);
        setIsPlaying(false);
        
        // Even on error, signal that webplayer attempted to initialize
        if (window.WallmuseInit && !cleanupInProgressRef.current) {
          logInfo("Initialization complete (with error), signaling ready");
          window.WallmuseInit.ready('webplayer');
        }
      }
    }, debounceDelay);

    // Cleanup function for this effect
    return () => {
      logInfo(`Cleaning up effect #${effectId}`);
      clearTimeout(handler);
      
      // Safely abort any pending requests
      try {
        if (abortController.current && typeof abortController.current.abort === 'function') {
          abortController.current.abort();
        }
      } catch (error) {
        console.error(`[WebPlayer] Error aborting fetch: ${error.message}`);
      }
      
      // Create a new controller for next time
      abortController.current = new AbortController();
    };
  }, [currentPlaylist, selectedTrack, montageOrderSignature]);

  // Handle volume changes independently
  useEffect(() => {
    if (volumeRef?.current !== undefined && videoElement.current && !cleanupInProgressRef.current) {
      logInfo(`Volume changed to ${volumeRef.current}, applying after delay`);
      
      // Add a delay to ensure we don't send commands too quickly
      const volumeTimeout = setTimeout(() => {
        if (!cleanupInProgressRef.current) {
          applyVolume();
        }
      }, 300);
      
      return () => clearTimeout(volumeTimeout);
    }
  }, [volumeRef?.current, house]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      logInfo("Component unmounting, performing final cleanup");
      cleanupPreviousPlayer();
    };
  }, []);

  const setupVideoMonitoring = (video) => {
    if (!video || cleanupInProgressRef.current) {
      logWarn("Cannot setup video monitoring - video is null or cleanup in progress");
      return;
    }
    
    // Add a data attribute to mark this video as monitored
    video.dataset.monitored = instanceId;
    
    logInfo(`Initial video state: ${video.paused ? 'paused' : 'playing'}, currentTime: ${video.currentTime}`);

    // Update state based on initial conditions
    if (!video.paused) {
      logInfo("Video is already playing, updating state");
      setIsPlaying(true);
      console.log('[WebPlayer isPlaying] set to true but not be updated immediately !paused', isPlaying);
      setLoading(false);
    }

    // Handle metadata loading
    const handleMetadataLoaded = () => {
      logInfo("Metadata loaded");
      
      // Add appropriate classes based on aspect ratio
      if (video.videoWidth / video.videoHeight === 1) {
        video.classList.add('square');
      } else if (video.videoWidth / video.videoHeight > 16 / 9) {
        video.classList.add('wide');
      } else {
        video.classList.add('full');
      }
      
      setLoading(false);
      // Apply volume after metadata is loaded
      applyVolume();
    };

    // Wait for video to be ready
    if (video.readyState >= 1) {
      logInfo("Video already has metadata");
      handleMetadataLoaded();
    } else {
      setLoading(true);
      video.addEventListener('loadedmetadata', handleMetadataLoaded, { once: true });
    }

    // Set up playback event handlers
    video.addEventListener('playing', () => {
      if (cleanupInProgressRef.current) return;
      logInfo("Video playing");
      setLoading(false);
      setIsPlaying(true);
      // Apply volume when video starts playing
      applyVolume();
    });

    video.addEventListener('pause', () => {
      if (cleanupInProgressRef.current) return;
      logInfo("Video paused");
      setIsPlaying(false);
    });

    video.addEventListener('ended', () => {
      if (cleanupInProgressRef.current) return;
      logInfo("Video ended");
      setIsPlaying(false);
    });

    video.addEventListener('error', (e) => {
      if (cleanupInProgressRef.current) return;
      logError("Video error:", e);
      setLoading(false);
      setIsPlaying(false);
    });

    // Add loading state for buffering
    video.addEventListener('waiting', () => {
      if (cleanupInProgressRef.current) return;
      logInfo("Video buffering");
      setLoading(true);
    });

    video.addEventListener('canplay', () => {
      if (cleanupInProgressRef.current) return;
      logInfo("Video can play");
      setLoading(false);
      // Apply volume when video can play
      applyVolume();
    });
  };

  return (
    <div
      className="web-player-content relative w-full h-full flex items-center justify-center bg-black"
      style={{ minHeight: '36.25vw' }}
    >
      {loading && <GradientCircularProgress />}
      <div
        ref={containerRef}
        className="w-full h-full flex items-center justify-center"
        style={{
          position: 'relative',
          overflow: 'visible',
          zIndex: 1
        }}
      />
    </div>
  );
};

WebPlayer.propTypes = {
  currentTheme: PropTypes.string,
  currentPlaylist: PropTypes.string,
  selectedTrack: PropTypes.string,
  onPlayStart: PropTypes.func,
  onPlayEnd: PropTypes.func,
  isPlaying: PropTypes.bool.isRequired,
  setIsPlaying: PropTypes.func.isRequired,
  house: PropTypes.string,
  volumeRef: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired
};

export default WebPlayer;